import { useState, useEffect } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { X, Loader2, ExternalLink } from "lucide-react";

const EXPERTISES = [
  { id: "fiscal", label: "Fiscal" },
  { id: "immeuble", label: "Immeuble" },
  { id: "entrepreneur", label: "Entrepreneur" },
  { id: "paie", label: "Paie" },
];

const DriveLogo = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 87.3 78" className={className}>
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
  </svg>
);

export default function DriveSendButton({ docType, docId, defaultExpertise = "fiscal", title = "Envoyer vers Google Drive", onSent }) {
  const [open, setOpen] = useState(false);
  const [expertise, setExpertise] = useState(defaultExpertise);
  const [masterId, setMasterId] = useState("");
  const [masters, setMasters] = useState([]);
  const [autoTarget, setAutoTarget] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setExpertise(defaultExpertise);
    api.get("/drive/masters")
      .then((r) => setMasters(r.data?.items || []))
      .catch(() => setMasters([]));
    api.get(`/drive/resolve/${docType}/${docId}`)
      .then((r) => setAutoTarget(r.data?.master_name || null))
      .catch(() => setAutoTarget(null));
  }, [open, defaultExpertise, docType, docId]);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/drive/send-document", {
        doc_type: docType,
        doc_id: docId,
        expertise,
        master_id: masterId || null,
      });
      const d = r.data;
      toast.success(`Classé dans ${d.folder_path || `${d.master_name} / ${d.folder}`}`, {
        description: d.filename,
        action: d.web_link ? { label: "Ouvrir le fichier", onClick: () => window.open(d.web_link, "_blank") } : (d.folder_link ? { label: "Ouvrir le dossier", onClick: () => window.open(d.folder_link, "_blank") } : undefined),
        duration: 12000,
      });
      onSent?.(d);
      setOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de l'envoi vers Drive");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 inline-flex items-center"
        title={title}
        data-testid={`drive-send-${docType}-${docId}`}
      >
        <DriveLogo />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="drive-send-dialog">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DriveLogo className="w-5 h-5" />
                <h3 className="font-semibold text-slate-800">Classer dans Google Drive</h3>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid="drive-dialog-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-slate-500 mb-2">Dans quel dossier classer ce document ?</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {EXPERTISES.map((ex) => (
                <button key={ex.id} type="button" onClick={() => setExpertise(ex.id)}
                  className={`border rounded-lg py-2 px-2 text-xs font-medium transition ${
                    expertise === ex.id ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`drive-expertise-${ex.id}`}>
                  {ex.label}
                </button>
              ))}
            </div>

            <label className="block text-xs text-slate-500 mb-1">Dossier client (master)</label>
            <select value={masterId} onChange={(e) => setMasterId(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm mb-1" data-testid="drive-master-select">
              <option value="">{autoTarget ? `Automatique : ${autoTarget}` : "Automatique — dossier du client lié"}</option>
              {masters.map((m) => (
                <option key={m.id} value={m.id}>{m.couple_display_name}</option>
              ))}
            </select>
            {autoTarget && !masterId && (
              <p className="text-[11px] text-slate-400 mb-3" data-testid="drive-auto-target-hint">
                Le document ira dans le dossier « <span className="font-semibold text-slate-600">{autoTarget}</span> ».
                Si ce n'est pas le bon, choisissez-le dans la liste ci-dessus.
              </p>
            )}
            {!autoTarget && !masterId && (
              <p className="text-[11px] text-amber-600 mb-3" data-testid="drive-no-auto-hint">
                Aucun dossier détecté automatiquement — choisissez le dossier dans la liste.
              </p>
            )}

            <button onClick={send} disabled={sending}
              className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="drive-send-confirm">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              {sending ? "Envoi en cours..." : "Envoyer vers Drive"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
